import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useFirestore } from '../../hooks/useFirestore';
import { getServices } from '../../firebase/firestore';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import './ManageServices.css';

const ManageServices = () => {
    const { data: services, loading, refresh } = useFirestore(useCallback(() => getServices(), []));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [formData, setFormData] = useState({
        titleAr: '',
        titleEn: '',
        descAr: '',
        descEn: '',
        icon: 'elevator',
        order: 1
    });

    const resetForm = () => {
        setFormData({
            titleAr: '',
            titleEn: '',
            descAr: '',
            descEn: '',
            icon: 'elevator',
            order: services ? services.length + 1 : 1
        });
        setEditingService(null);
    };

    const handleOpenModal = (service = null) => {
        if (service) {
            setEditingService(service);
            setFormData({
                titleAr: service.titleAr,
                titleEn: service.titleEn,
                descAr: service.descAr,
                descEn: service.descEn,
                icon: service.icon,
                order: service.order
            });
        } else {
            resetForm();
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingService) {
                await updateDoc(doc(db, 'services', editingService.id), formData);
            } else {
                await addDoc(collection(db, 'services'), formData);
            }
            setIsModalOpen(false);
            refresh();
        } catch (error) {
            console.error('Error saving service:', error);
            alert('فشل حفظ الخدمة. يرجى المحاولة مرة أخرى.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذه الخدمة؟')) {
            try {
                await deleteDoc(doc(db, 'services', id));
                refresh();
            } catch (error) {
                console.error('Error deleting service:', error);
                alert('فشل حذف الخدمة.');
            }
        }
    };

    if (loading && !services) return <Loading />;

    return (
        <div className="manage-services">
            <div className="admin-page-header">
                <h1>إدارة الخدمات</h1>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    <PlusIcon className="btn-icon" />
                    <span>إضافة خدمة</span>
                </Button>
            </div>

            <div className="admin-table-container shadow-premium">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>الترتيب</th>
                            <th>العنوان (عربي)</th>
                            <th>العنوان (English)</th>
                            <th>الأيقونة</th>
                            <th>الإجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {services?.map((service) => (
                            <tr key={service.id}>
                                <td>{service.order}</td>
                                <td>{service.titleAr}</td>
                                <td>{service.titleEn}</td>
                                <td><span className="icon-badge">{service.icon}</span></td>
                                <td className="actions-cell">
                                    <button className="action-btn edit" onClick={() => handleOpenModal(service)}>
                                        <PencilIcon />
                                    </button>
                                    <button className="action-btn delete" onClick={() => handleDelete(service.id)}>
                                        <TrashIcon />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="modal-content"
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                        >
                            <div className="modal-header">
                                <h2>{editingService ? 'تعديل خدمة' : 'إضافة خدمة جديدة'}</h2>
                                <button className="close-btn" onClick={() => setIsModalOpen(false)}>
                                    <XMarkIcon />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="admin-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>العنوان بالعربية</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.titleAr}
                                            onChange={(e) => setFormData({ ...formData, titleAr: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Title (English)</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.titleEn}
                                            onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>الوصف بالعربية</label>
                                    <textarea
                                        rows="3"
                                        required
                                        value={formData.descAr}
                                        onChange={(e) => setFormData({ ...formData, descAr: e.target.value })}
                                    ></textarea>
                                </div>

                                <div className="form-group">
                                    <label>Description (English)</label>
                                    <textarea
                                        rows="3"
                                        required
                                        value={formData.descEn}
                                        onChange={(e) => setFormData({ ...formData, descEn: e.target.value })}
                                    ></textarea>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>الأيقونة</label>
                                        <select
                                            value={formData.icon}
                                            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                        >
                                            <option value="elevator">Elevator (Passenger)</option>
                                            <option value="truck">Truck (Freight)</option>
                                            <option value="stairs">Stairs (Escalator)</option>
                                            <option value="walk">Walk (Moving Walk)</option>
                                            <option value="wrench">Wrench (Maintenance)</option>
                                            <option value="refresh">Refresh (Modernization)</option>
                                            <option value="shield">Shield (Safety)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>الترتيب</label>
                                        <input
                                            type="number"
                                            value={formData.order}
                                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                        />
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <Button type="button" variant="ghost" onClick={() => setIsModalOpen(false)}>
                                        إلغاء
                                    </Button>
                                    <Button type="submit" variant="primary">
                                        {editingService ? 'تحديث' : 'إضافة'}
                                    </Button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ManageServices;
